import React from 'react';

import { Card, Icon, Separator, Text, VStack } from '@betterment/samba-react';
import { Box, Flash, Flex } from '@betterment/style-closet-react';

import { AccountChecklistTask, IncompleteTask } from './AccountChecklistTask';
import { useAccount } from './AccountProvider';
import CheckingCardActivationErrorMessage from './CheckingCardActivationErrorMessage';

import TwoCardsImage from '../../images/two_cards_right_corner.png';
import { urlBuilder } from '../../Shared/urlBuilder';

interface Props {
  isCardVerified: boolean;
  hasCardError: boolean;
  hasExistingTransactions: boolean;
  cardExpectedDeliveryAt: string | React.ReactElement;
}

const CardErrorFlash: React.FC<React.PropsWithChildren<unknown>> = () => {
  return (
    <Box m="4" pb="4">
      <Flash variant="error" icon={false}>
        <Flex m="5">
          <Box mr="3">
            <Icon name="ExclamationCircle" size="small" color="icon.critical" />
          </Box>
          <CheckingCardActivationErrorMessage />
        </Flex>
      </Flash>
    </Box>
  );
};

const AccountSetupChecklist: React.FC<React.PropsWithChildren<Props>> = ({
  isCardVerified,
  hasCardError,
  hasExistingTransactions,
  cardExpectedDeliveryAt,
}) => {
  const { account } = useAccount();

  return (
    <Card
      css={{
        px: 'space.300',
        mb: 'space.200',
        backgroundRepeat: 'no-repeat',
      }}
      style={{
        backgroundImage: !hasCardError ? `url(${TwoCardsImage})` : 'none',
        backgroundPosition: 'right bottom',
      }}
    >
      <Card.Header css={{ py: 'space.300' }}>
        <Text
          css={{
            fontSize: 'fontSize.200',
            fontWeight: 'medium',
            margin: 'space.0',
          }}
        >
          Account setup
        </Text>
      </Card.Header>
      <Separator />
      <Card.Content css={{ py: 'space.300' }}>
        {hasCardError ? (
          <CardErrorFlash />
        ) : (
          <VStack role="list" css={{ justifyContent: 'space-between' }}>
            <AccountChecklistTask iconName="checklistMarkCompleted">Order your debit card</AccountChecklistTask>
            {hasExistingTransactions ? (
              <AccountChecklistTask iconName="checklistMarkCompleted">Fund your account</AccountChecklistTask>
            ) : (
              <IncompleteTask
                eventName="UserTaskFundAccount"
                url={urlBuilder.getCheckingFundingOptions.absolute({ accountId: account.id })}
                title="Fund your account"
              />
            )}
            {isCardVerified ? (
              <AccountChecklistTask iconName="checklistMarkCompleted">Activate your card</AccountChecklistTask>
            ) : (
              <IncompleteTask
                eventName="UserTaskActivateCard"
                url={urlBuilder.getAppCheckingCardSetup.absolute()}
                title="Activate your card"
                subText={`Delivered by ${cardExpectedDeliveryAt as string}`}
              />
            )}
          </VStack>
        )}
      </Card.Content>
    </Card>
  );
};

export default AccountSetupChecklist;
